import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from './useAuth';

export interface Workspace {
  workspaceId: string;
  userId: string;
  name: string;
  description?: string;
  slug?: string;
  logoUrl?: string;
  companyName?: string;
  industry?: string;
  tagline?: string;
  website?: string;
  twitter?: string;
  foundedYear?: number;
  pricingTiers?: Array<{ name: string; price: number; interval: 'month' | 'year'; features?: string[] }>;
  pricingUrl?: string;
  testimonialCount?: number;
  rating?: number;
  advanced?: { monthlyTraffic?: number; githubStars?: number; twitterFollowers?: number };
  stripeConnected: boolean;
  stripeAccountId?: string;
  publicProfileEnabled: boolean;
  directoryListed: boolean;
  directorySlug?: string;
  createdAt: string;
  updatedAt: string;
}

type NullableFields = 'description' | 'website' | 'twitter' | 'foundedYear' | 'pricingUrl' | 'testimonialCount' | 'rating' | 'advanced';
type UpdateWorkspaceData = Partial<Pick<Workspace, 'name' | 'companyName' | 'industry' | 'tagline' | 'slug' | 'directoryListed' | 'directorySlug'>>
  & { [K in NullableFields]?: Workspace[K] | null };

interface WorkspaceContextType {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  isLoading: boolean;
  error: string | null;
  setCurrentWorkspace: (workspace: Workspace) => void;
  createWorkspace: (name: string, description?: string) => Promise<Workspace>;
  updateWorkspace: (workspaceId: string, data: UpdateWorkspaceData) => Promise<void>;
  deleteWorkspace: (workspaceId: string) => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
  hasWorkspaces: boolean;
  showCreateModal: boolean;
  modalExistingWorkspaceId: string | null;
  openCreateModal: () => void;
  openStripeModal: (workspaceId: string) => void;
  closeCreateModal: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

const CURRENT_WORKSPACE_KEY = 'plevits_current_workspace_id';

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspaceState] = useState<Workspace | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { refreshUser } = useAuth();
  const [showCreateModal, setShowCreateModalRaw] = useState(false);
  const [modalExistingWorkspaceId, setModalExistingWorkspaceId] = useState<string | null>(null);

  const openCreateModal = useCallback(() => {
    setModalExistingWorkspaceId(null);
    setShowCreateModalRaw(true);
  }, []);

  const openStripeModal = useCallback((workspaceId: string) => {
    setModalExistingWorkspaceId(workspaceId);
    setShowCreateModalRaw(true);
  }, []);

  const closeCreateModal = useCallback(() => {
    setModalExistingWorkspaceId(null);
    setShowCreateModalRaw(false);
  }, []);

  const fetchWorkspaces = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      // API returns 'id' but we use 'workspaceId' internally
      interface ApiWorkspace {
        id: string;
        name: string;
        slug: string;
        companyName?: string;
        industry?: string;
        tagline?: string;
        description?: string;
        website?: string;
        twitter?: string;
        foundedYear?: number;
        pricingTiers?: Array<{ name: string; price: number; interval: 'month' | 'year'; features?: string[] }>;
        pricingUrl?: string;
        testimonialCount?: number;
        rating?: number;
        advanced?: { monthlyTraffic?: number; githubStars?: number; twitterFollowers?: number };
        plan: string;
        stripeConnected: boolean;
        publicProfileEnabled: boolean;
        directoryListed: boolean;
        directorySlug?: string;
        role: string;
        createdAt: string;
      }
      const response = await api.get<{ workspaces: ApiWorkspace[] }>('/workspaces');
      const workspaces: Workspace[] = response.workspaces.map(w => ({
        workspaceId: w.id,
        userId: '', // Not returned by list API
        name: w.name,
        slug: w.slug,
        companyName: w.companyName,
        industry: w.industry,
        tagline: w.tagline,
        description: w.description,
        website: w.website,
        twitter: w.twitter,
        foundedYear: w.foundedYear,
        pricingTiers: w.pricingTiers,
        pricingUrl: w.pricingUrl,
        testimonialCount: w.testimonialCount,
        rating: w.rating,
        advanced: w.advanced,
        stripeConnected: w.stripeConnected,
        publicProfileEnabled: w.publicProfileEnabled,
        directoryListed: w.directoryListed || false,
        directorySlug: w.directorySlug,
        createdAt: w.createdAt,
        updatedAt: w.createdAt,
      }));
      setWorkspaces(workspaces);
      // Keep current workspace state consistent with server truth.
      if (workspaces.length === 0) {
        setCurrentWorkspaceState(null);
        localStorage.removeItem(CURRENT_WORKSPACE_KEY);
      }
      return workspaces;
    } catch (err) {
      setError('Failed to load workspaces');
      console.error('Error fetching workspaces:', err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize workspaces and restore current workspace from localStorage
  useEffect(() => {
    const initializeWorkspaces = async () => {
      const fetchedWorkspaces = await fetchWorkspaces();

      if (fetchedWorkspaces.length > 0) {
        const savedWorkspaceId = localStorage.getItem(CURRENT_WORKSPACE_KEY);
        const savedWorkspace = savedWorkspaceId
          ? fetchedWorkspaces.find(w => w.workspaceId === savedWorkspaceId)
          : null;

        setCurrentWorkspaceState(savedWorkspace || fetchedWorkspaces[0]);
      }

      setIsLoading(false);
    };

    initializeWorkspaces();
  }, [fetchWorkspaces]);

  const setCurrentWorkspace = useCallback((workspace: Workspace) => {
    setCurrentWorkspaceState(workspace);
    localStorage.setItem(CURRENT_WORKSPACE_KEY, workspace.workspaceId);
  }, []);

  const createWorkspace = useCallback(async (name: string, description?: string): Promise<Workspace> => {
    // API returns 'id' but we use 'workspaceId' internally
    const apiResponse = await api.post<{ id: string; name: string; slug: string; stripeConnected: boolean; publicProfileEnabled: boolean; directoryListed: boolean; plan: string; createdAt: string; ownerId: string }>('/workspaces', { name, description });
    const workspace: Workspace = {
      workspaceId: apiResponse.id,
      userId: apiResponse.ownerId,
      name: apiResponse.name,
      description,
      slug: apiResponse.slug,
      stripeConnected: apiResponse.stripeConnected,
      publicProfileEnabled: apiResponse.publicProfileEnabled,
      directoryListed: apiResponse.directoryListed || false,
      createdAt: apiResponse.createdAt,
      updatedAt: apiResponse.createdAt,
    };
    setWorkspaces(prev => [...prev, workspace]);
    setCurrentWorkspace(workspace);
    // Refresh user to update workspaceCount for limit enforcement
    refreshUser().catch(() => {});
    return workspace;
  }, [setCurrentWorkspace, refreshUser]);

  const updateWorkspace = useCallback(async (
    workspaceId: string,
    data: UpdateWorkspaceData
  ): Promise<void> => {
    await api.patch(`/workspaces/${workspaceId}`, data);
    await fetchWorkspaces();
  }, [fetchWorkspaces]);

  const deleteWorkspace = useCallback(async (workspaceId: string): Promise<void> => {
    await api.delete(`/workspaces/${workspaceId}`);
    const updatedWorkspaces = await fetchWorkspaces();
    // Refresh user to update workspaceCount for limit enforcement
    refreshUser().catch(() => {});

    // If deleted workspace was currently selected
    if (currentWorkspace?.workspaceId === workspaceId) {
      if (updatedWorkspaces.length > 0) {
        // Switch to first available workspace
        setCurrentWorkspace(updatedWorkspaces[0]);
      } else {
        // No workspaces left - clear current workspace and localStorage
        setCurrentWorkspaceState(null);
        localStorage.removeItem(CURRENT_WORKSPACE_KEY);
      }
    }
  }, [currentWorkspace, fetchWorkspaces, setCurrentWorkspace, refreshUser]);

  const refreshWorkspaces = useCallback(async () => {
    await fetchWorkspaces();
  }, [fetchWorkspaces]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        currentWorkspace,
        isLoading,
        error,
        setCurrentWorkspace,
        createWorkspace,
        updateWorkspace,
        deleteWorkspace,
        refreshWorkspaces,
        hasWorkspaces: workspaces.length > 0,
        showCreateModal,
        modalExistingWorkspaceId,
        openCreateModal,
        openStripeModal,
        closeCreateModal,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
